// Flow Builder visuale (Sprint 6)
// React Flow nodale: sequenza nodi domanda + nodi outcome + skip_if rappresentati come archi.
// L'editor consuma e mantiene `flow.visual_layout` (posizioni nodi/edges custom).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  ReactFlowProvider,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  Handle,
  Position,
  type Connection,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
  type NodeProps,
  type ReactFlowInstance,
} from '@xyflow/react';
import { useQueryClient } from '@tanstack/react-query';
import type { FlowOutcome, FlowQuestion, FlowWithRelations, VisualLayout } from '@audiq/shared';
import { api } from '../../lib/api';
import { TestModeModal } from './TestModeModal';

interface Props {
  flow: FlowWithRelations;
}

// Usiamo Node base con `data` libera; il tipo del campo è validato runtime nei custom node component.
type AudiqNode = Node;
type AudiqEdge = Edge;

const NODE_W = 240;
const NODE_H = 96;
const ROW_GAP_X = 60;
const ROW_GAP_Y = 60;
const COL_X = 360;

// =====================================================================
// Autolayout: griglia semplice — questions in riga, outcomes in colonna a destra.
// =====================================================================
function buildAutoLayout(flow: FlowWithRelations): VisualLayout {
  const nodes: VisualLayout['nodes'] = [];
  const edges: VisualLayout['edges'] = [];

  // Start
  nodes.push({ id: 'start', type: 'start', position: { x: 0, y: 0 } });

  // Questions: riga orizzontale a y=0
  const sorted = [...flow.questions].sort((a, b) => a.sort_order - b.sort_order);
  let prevId = 'start';
  sorted.forEach((q, i) => {
    const x = (i + 1) * (NODE_W + ROW_GAP_X);
    nodes.push({
      id: `q:${q.id}`,
      type: 'question',
      position: { x, y: 0 },
      data: { questionId: q.id },
    });
    edges.push({
      id: `e:${prevId}->q:${q.id}`,
      source: prevId,
      target: `q:${q.id}`,
      data: { kind: 'flow' },
    });
    prevId = `q:${q.id}`;
  });

  // End node a fine riga
  const endX = (sorted.length + 1) * (NODE_W + ROW_GAP_X);
  nodes.push({ id: 'end', type: 'end', position: { x: endX, y: 0 } });
  edges.push({ id: `e:${prevId}->end`, source: prevId, target: 'end', data: { kind: 'flow' } });

  // Outcomes: colonna a destra di end
  flow.outcomes
    .slice()
    .sort((a, b) => a.priority - b.priority)
    .forEach((o, i) => {
      nodes.push({
        id: `o:${o.id}`,
        type: 'outcome',
        position: { x: endX + COL_X, y: i * (NODE_H + ROW_GAP_Y) },
        data: { outcomeId: o.id },
      });
      edges.push({
        id: `e:end->o:${o.id}`,
        source: 'end',
        target: `o:${o.id}`,
        data: { kind: 'flow' },
      });
    });

  return { nodes, edges };
}

/** Combina layout salvato + flow corrente — riusa posizioni note, autolayout per nodi nuovi. */
function reconcileLayout(saved: VisualLayout, flow: FlowWithRelations): VisualLayout {
  const auto = buildAutoLayout(flow);
  if (!saved.nodes || saved.nodes.length === 0) return auto;

  const savedById = new Map(saved.nodes.map((n) => [n.id, n]));
  const reconciledNodes: VisualLayout['nodes'] = auto.nodes.map((n) => {
    const s = savedById.get(n.id);
    if (s) return { ...n, position: s.position };
    return n;
  });

  // Edges: preferiamo autolayout (rispetta stato corrente del flow), ma teniamo eventuali archi custom (kind=skip).
  const customEdges = (saved.edges ?? []).filter((e) => e.data?.['kind'] === 'skip');
  return { nodes: reconciledNodes, edges: [...auto.edges, ...customEdges] };
}

// =====================================================================
// Custom Node components
// =====================================================================

function StartNode() {
  return (
    <div
      className="audiq flex items-center justify-center rounded-full"
      style={{
        width: 80,
        height: 80,
        background: 'var(--primary)',
        color: '#fff',
        boxShadow: '0 2px 12px rgba(31,36,33,0.12)',
        border: '3px solid var(--paper)',
        fontWeight: 700,
        fontSize: 13,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
      }}
    >
      Inizio
      <Handle type="source" position={Position.Right} id="out" />
    </div>
  );
}

function EndNode() {
  return (
    <div
      className="audiq flex items-center justify-center rounded-full"
      style={{
        width: 80,
        height: 80,
        background: 'var(--accent)',
        color: 'var(--ink)',
        boxShadow: '0 2px 12px rgba(31,36,33,0.12)',
        border: '3px solid var(--paper)',
        fontWeight: 700,
        fontSize: 13,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
      }}
    >
      <Handle type="target" position={Position.Left} id="in" />
      Fine
      <Handle type="source" position={Position.Right} id="out" />
    </div>
  );
}

function QuestionNode({ data }: NodeProps) {
  const q = (data as { question?: FlowQuestion }).question;
  if (!q) return null;
  return (
    <div
      style={{
        width: NODE_W,
        background: 'var(--paper)',
        border: '1px solid var(--line-strong)',
        borderRadius: 12,
        boxShadow: '0 2px 6px rgba(31,36,33,0.05)',
        padding: '10px 12px',
        position: 'relative',
      }}
    >
      <Handle type="target" position={Position.Left} id="in" />
      <div
        className="mono"
        style={{
          fontSize: 11,
          color: 'var(--mute)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          marginBottom: 4,
        }}
      >
        Q · {q.data_type}
        {q.required && (
          <span style={{ color: 'var(--accent-2)', marginInlineStart: 6 }}>obbligatoria</span>
        )}
      </div>
      <div
        style={{
          fontSize: 13,
          color: 'var(--ink)',
          lineHeight: 1.3,
          maxHeight: 56,
          overflow: 'hidden',
        }}
      >
        {q.objective}
      </div>
      {q.tags && q.tags.length > 0 && (
        <div className="mono" style={{ fontSize: 10, color: 'var(--mute)', marginTop: 4 }}>
          {q.tags.slice(0, 3).join(' · ')}
        </div>
      )}
      <Handle type="source" position={Position.Right} id="out" />
    </div>
  );
}

function OutcomeNode({ data }: NodeProps) {
  const o = (data as { outcome?: FlowOutcome }).outcome;
  if (!o) return null;
  const conds = o.conditions;
  const condCount = conds && 'rules' in conds ? conds.rules.length : 0;
  return (
    <div
      style={{
        width: NODE_W,
        background: 'var(--paper)',
        border: '1px solid var(--accent)',
        borderRadius: 12,
        boxShadow: '0 2px 6px rgba(31,36,33,0.05)',
        padding: '10px 12px',
        position: 'relative',
      }}
    >
      <Handle type="target" position={Position.Left} id="in" />
      <div
        className="mono"
        style={{
          fontSize: 11,
          color: 'var(--accent-2)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          marginBottom: 4,
        }}
      >
        Esito · prio {o.priority}
      </div>
      <div
        style={{
          fontSize: 13,
          color: 'var(--ink)',
          fontWeight: 600,
          lineHeight: 1.3,
        }}
      >
        {o.name}
      </div>
      <div className="mono" style={{ fontSize: 10, color: 'var(--mute)', marginTop: 4 }}>
        {condCount} condizion{condCount === 1 ? 'e' : 'i'}
        {' · '}
        {o.action.type}
      </div>
    </div>
  );
}

const nodeTypes = {
  start: StartNode,
  end: EndNode,
  question: QuestionNode,
  outcome: OutcomeNode,
};

// =====================================================================
// Builder shell
// =====================================================================
export function FlowBuilder({ flow }: Props) {
  return (
    <ReactFlowProvider>
      <FlowBuilderInner flow={flow} />
    </ReactFlowProvider>
  );
}

function FlowBuilderInner({ flow }: Props) {
  const qc = useQueryClient();
  const [showTestModal, setShowTestModal] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const reactFlowRef = useRef<ReactFlowInstance | null>(null);

  // Hydrate iniziale: combina flow.visual_layout salvato con autolayout per nodi nuovi.
  const initialLayout = useMemo(
    () => reconcileLayout(flow.visual_layout, flow),
    [flow.visual_layout, flow],
  );

  const initialNodes = useMemo<AudiqNode[]>(() => {
    return initialLayout.nodes.map<AudiqNode>((n) => {
      if (n.type === 'start') {
        return { id: n.id, type: 'start', position: n.position, data: { kind: 'start' } };
      }
      if (n.type === 'end') {
        return { id: n.id, type: 'end', position: n.position, data: { kind: 'end' } };
      }
      if (n.type === 'question') {
        const qid = (n.data as { questionId?: string } | undefined)?.questionId ?? n.id.slice(2);
        const question = flow.questions.find((q) => q.id === qid);
        if (!question) {
          // Nodo orfano — render comunque per debug (l'autolayout dopo lo rimuoverà)
          return {
            id: n.id,
            type: 'question',
            position: n.position,
            data: {
              kind: 'question',
              question: {
                id: qid,
                flow_id: flow.id,
                sort_order: 0,
                objective: '(domanda eliminata)',
                ask_examples: [],
                data_type: 'text',
                data_semantic: null,
                choices: null,
                validation: null,
                tags: [],
                flags_to_trigger: [],
                follow_up_if_vague: null,
                required: false,
                skip_if: null,
              } as FlowQuestion,
            },
          };
        }
        return {
          id: n.id,
          type: 'question',
          position: n.position,
          data: { kind: 'question', question },
        };
      }
      // outcome
      const oid = (n.data as { outcomeId?: string } | undefined)?.outcomeId ?? n.id.slice(2);
      const outcome = flow.outcomes.find((o) => o.id === oid);
      if (!outcome) {
        return {
          id: n.id,
          type: 'outcome',
          position: n.position,
          data: {
            kind: 'outcome',
            outcome: {
              id: oid,
              flow_id: flow.id,
              sort_order: 0,
              name: '(esito eliminato)',
              description: '',
              conditions: { op: 'AND', rules: [] },
              action: { type: 'info', content: '', meta: null },
              priority: 100,
            } as FlowOutcome,
          },
        };
      }
      return {
        id: n.id,
        type: 'outcome',
        position: n.position,
        data: { kind: 'outcome', outcome },
      };
    });
  }, [initialLayout, flow]);

  const initialEdges = useMemo<AudiqEdge[]>(
    () =>
      initialLayout.edges.map((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle ?? undefined,
        targetHandle: e.targetHandle ?? undefined,
        label: e.label,
        animated: e.data?.['kind'] === 'skip',
        style:
          e.data?.['kind'] === 'skip'
            ? { stroke: 'var(--accent-2)', strokeDasharray: '6,3' }
            : undefined,
        data: e.data as AudiqEdge['data'],
      })),
    [initialLayout],
  );

  const [nodes, setNodes] = useState<AudiqNode[]>(initialNodes);
  const [edges, setEdges] = useState<AudiqEdge[]>(initialEdges);

  // Quando il flow upstream cambia (es. dopo edit in altro tab), riallinea.
  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges]);

  // ====== Persist debounced ======
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const persistLayout = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const layout: VisualLayout = {
        nodes: nodes.map((n) => ({
          id: n.id,
          type: n.type ?? 'question',
          position: n.position,
          data:
            n.type === 'question'
              ? { questionId: (n.data as { question?: FlowQuestion }).question?.id ?? '' }
              : n.type === 'outcome'
                ? { outcomeId: (n.data as { outcome?: FlowOutcome }).outcome?.id ?? '' }
                : undefined,
        })),
        edges: edges.map((e) => ({
          id: e.id,
          source: e.source,
          target: e.target,
          sourceHandle: e.sourceHandle ?? null,
          targetHandle: e.targetHandle ?? null,
          label: typeof e.label === 'string' ? e.label : undefined,
          data: e.data ?? undefined,
        })),
      };
      try {
        await api.patchVisualLayout(flow.id, layout);
        setSavedAt(new Date());
      } catch {
        // best-effort: errore non bloccante. Sprint 9 può aggiungere toast.
      }
    }, 1000);
  }, [nodes, edges, flow.id]);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      setNodes((ns) => applyNodeChanges(changes, ns) as AudiqNode[]);
      // Persist solo se il cambio è di posizione (final).
      const hasPositionChange = changes.some(
        (c) => c.type === 'position' && (c as { dragging?: boolean }).dragging === false,
      );
      if (hasPositionChange) persistLayout();
    },
    [persistLayout],
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      setEdges((es) => applyEdgeChanges(changes, es) as AudiqEdge[]);
      persistLayout();
    },
    [persistLayout],
  );

  const onConnect = useCallback(
    (conn: Connection) => {
      // Connect manuale: se è fra due question creiamo un edge di tipo "skip" (rappresenta uno skip_if).
      const sourceNode = nodes.find((n) => n.id === conn.source);
      const targetNode = nodes.find((n) => n.id === conn.target);
      const isSkip =
        sourceNode?.type === 'question' &&
        targetNode?.type === 'question' &&
        sourceNode.id !== targetNode.id;
      const newEdge: AudiqEdge = {
        id: `e:${conn.source}->${conn.target}:${Date.now()}`,
        source: conn.source!,
        target: conn.target!,
        sourceHandle: conn.sourceHandle ?? undefined,
        targetHandle: conn.targetHandle ?? undefined,
        animated: isSkip,
        style: isSkip ? { stroke: 'var(--accent-2)', strokeDasharray: '6,3' } : undefined,
        data: { kind: isSkip ? 'skip' : 'flow' },
      };
      setEdges((es) => addEdge(newEdge, es) as AudiqEdge[]);
      persistLayout();
    },
    [nodes, persistLayout],
  );

  const onResetLayout = useCallback(async () => {
    const auto = buildAutoLayout(flow);
    await api.patchVisualLayout(flow.id, auto);
    qc.invalidateQueries({ queryKey: ['flow', flow.id] });
  }, [flow, qc]);

  return (
    <div
      style={{
        height: '70vh',
        minHeight: 480,
        border: '1px solid var(--line)',
        borderRadius: 12,
        background: 'var(--paper)',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <div
        className="audiq-builder-toolbar"
        style={{
          position: 'absolute',
          top: 8,
          insetInlineStart: 8,
          zIndex: 5,
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          background: 'var(--paper)',
          padding: '6px 10px',
          border: '1px solid var(--line)',
          borderRadius: 8,
          boxShadow: '0 1px 4px rgba(31,36,33,0.04)',
        }}
      >
        <button
          type="button"
          onClick={() => setShowTestModal(true)}
          className="rounded-md px-3 py-1 text-sm"
          style={{ background: 'var(--primary)', color: '#fff' }}
        >
          ▶ Test
        </button>
        <button
          type="button"
          onClick={onResetLayout}
          className="rounded-md px-3 py-1 text-sm"
          style={{ background: 'var(--soft)', color: 'var(--ink)' }}
        >
          Reset layout
        </button>
        <span className="mono" style={{ fontSize: 11, color: 'var(--mute)' }}>
          {savedAt ? `Salvato ${savedAt.toLocaleTimeString('it')}` : 'Auto-save attivo'}
        </span>
      </div>

      <div
        className="audiq-builder-legenda"
        style={{
          position: 'absolute',
          bottom: 8,
          insetInlineStart: 8,
          zIndex: 5,
          background: 'var(--paper)',
          padding: '6px 10px',
          border: '1px solid var(--line)',
          borderRadius: 8,
          fontSize: 11,
          color: 'var(--mute)',
          fontFamily: 'var(--font-mono)',
        }}
      >
        Trascina i nodi · Click su una domanda/esito apre il dettaglio nei tab
      </div>

      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onInit={(instance) => {
          reactFlowRef.current = instance;
        }}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={16} size={1} />
        <Controls position="bottom-right" />
        <MiniMap pannable zoomable nodeColor={miniMapNodeColor} />
      </ReactFlow>

      {showTestModal && <TestModeModal flow={flow} onClose={() => setShowTestModal(false)} />}
    </div>
  );
}

function miniMapNodeColor(node: Node): string {
  if (node.type === 'start') return 'var(--primary)';
  if (node.type === 'end') return 'var(--accent)';
  if (node.type === 'outcome') return 'var(--accent)';
  return 'var(--primary-soft)';
}
